// Sign-in for the console.
//
// ★ Since 2026-10-16 (plan 27, approved by Omar: "Renewal card storage: implement
//   it now") OUR server runs the sign-in. It keeps the renewal card in a protected
//   cookie on the console's own address, which no script on the page can read, and
//   hands the console only a 1-hour pass, kept in memory. Auth0's browser library
//   is gone from the console.
//
// The console has its own confidential Auth0 application on the SAME tenant and the
// SAME audience as apps/fe — not a second trust domain. The gateway verifies both
// apps' passes against one JWKS (services/gateway/src/auth/staff.ts), and staff are
// recognised by their roles claim, not by which application they signed in through.
//
// The console signs in with a FULL PAGE, as it always has: `/auth/login` on this
// site, Auth0, then back to the path the person started from. That path travels to
// our server and back (`return=`), checked there to be a path on this site.

// Side-effect imports: classic scripts that publish on `window`. They have to stay
// classic scripts because apps/fe loads them with plain <script src> tags, and the
// same rule has to run in both apps or it drifts — which is how the return-leg
// defect of 2026-08-24 was born.
import "../../fe/src/auth-callback-classify.js";
import "../../fe/src/session-client-core.js";
import type { AuthCallbackNotice } from "../../fe/src/auth-callback-classify.js";
import type { SessionClient } from "../../fe/src/session-client-core.js";

export type { AuthCallbackNotice, SessionClient };

/**
 * Start the console's half of the sign-in service.
 *
 * It cannot throw for a sign-in that did not finish: that comes back as a
 * `notice`, the app boots signed out, and app.tsx shows the notice above the Sign
 * in button that was always the remedy (Omar hit the throwing version on staging,
 * 2026-08-24).
 */
export function initAuth(): {
  session: SessionClient;
  notice: AuthCallbackNotice | null;
} {
  const notice = window.consumeSignInReturn({
    href: window.location.href,
    history: window.history,
  });
  const session = window.SessionClientCore.createSessionClient({
    win: window,
    fetchFn: (url, init) => fetch(url, init),
  });
  return { session, notice };
}

/** The full-page sign-in, coming back to where the person was. */
export function login(): void {
  const here = window.location.pathname + window.location.search;
  window.location.assign(`/auth/login?mode=page&return=${encodeURIComponent(here)}`);
}

/**
 * Sign out: our server cancels the card and clears the cookie, then Auth0's own
 * sign-out ends Auth0's session and comes back to the console.
 */
export async function logout(session: SessionClient): Promise<void> {
  let next = `${window.location.origin}/`;
  try {
    const { logoutUrl } = await session.signOut();
    if (logoutUrl) next = logoutUrl;
  } catch (err) {
    // The cookie may still be there; going home shows the person what is true.
    console.warn("could not sign out", err);
  }
  window.location.assign(next);
}
